<template>
  <div class="article">
    <form @submit.prevent="onSubmit">
      <section class="section">
        <div class="level is-mobile">
          <div class="level-left">
            <div class="level-item">
              <nav class="breadcrumb" aria-label="breadcrumbs">
                <ul>
                  <li
                    v-for="(item, key) in breadcrumb"
                    :key="item.title"
                    :class="{'is-active': key === breadcrumb.length - 1}"
                    >
                    <router-link :to="item.to" v-if="item.to">
                      {{item.title}}
                    </router-link>
                    <a v-else>{{item.title}}</a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <router-link class="button" :to="{name: 'article'}">
                Back
              </router-link>
            </div>
          </div>
        </div>
      </section>
      <section class="section">
        <b-tabs>
          <b-tab-item label="Content">
            <p class="title">Content</p>
            <div class="field">
              <b-checkbox v-model="published" :disabled="isDisabled">
                Published
              </b-checkbox>
            </div>
            <b-field label="Active At">
              <b-datepicker
                placeholder="Click to select..."
                icon="calendar"
                v-model="activeAt"
                :disabled="isDisabled"
              />
            </b-field>
            <b-field label="Slug">
              <b-field>
                <p class="control">
                  <span class="button is-static">
                    <b-icon pack="fas" icon="globe" />
                    <span>sepsakon.com/articles/</span>
                  </span>
                </p>
                <b-input v-model="slug" :disabled="isDisabled" class="is-expanded"/>
              </b-field>
            </b-field>
            <b-field label="Title">
              <b-input v-model="title" :disabled="isDisabled" />
            </b-field>
            <b-field label="Description">
              <b-input v-model="description" type="textarea" :disabled="isDisabled" />
            </b-field>
            <b-field label="Detail">
              <ckeditor
                type="classic"
                :editors="editors"
                v-model="edtiorDetail"
                ref="detail"
                @input="onEditorInput"
                :upload-adapter="uploadAdapter"
              />
            </b-field>
            <b-field label="Tags">
                <b-taginput
                    v-model="tags"
                    :data="filteredTags"
                    autocomplete
                    :allow-new="true"
                    field="name"
                    icon="tag"
                    placeholder="Add a tag"
                    @typing="getFilteredTags"
                    :disabled="isDisabled"
                />
            </b-field>
          </b-tab-item>
          <b-tab-item label="Media">
            <p class="title">Media</p>
            <b-field
              :label="
                `Thumbnail (${recommendedSize(media.thumbnail.recommendedSize)})`
              "
              >
              <b-field class="file">
                <b-upload v-model="media.thumbnail.file" accept="image/*">
                  <a class="button is-primary">
                    <b-icon icon="upload"></b-icon>
                    <span>Click to upload</span>
                  </a>
                </b-upload>
                <span class="file-name" v-if="media.thumbnail.file">
                  {{ media.thumbnail.file.name }}
                </span>
              </b-field>
            </b-field>
            <b-field>
              <div class="image-preview">
                <div
                  class="image"
                  v-if="media.thumbnail.file || media.thumbnail.asset.url"
                  >
                  <img
                    ref="thumbnail"
                    :src="media.thumbnail.file
                      ? getImageFromBlob(media.thumbnail.file, 'thumbnail')
                      : media.thumbnail.asset.url"
                    :style="media.thumbnail.recommendedSize | maxSize"
                  >
                </div>
              </div>
            </b-field>
            <b-field
              :label="
                `Banner Desktop (${recommendedSize(media.bannerDesktop.recommendedSize)})`
                "
              >
              <b-field class="file">
                <b-upload v-model="media.bannerDesktop.file" accept="image/*">
                  <a class="button is-primary">
                    <b-icon icon="upload"></b-icon>
                    <span>Click to upload</span>
                  </a>
                </b-upload>
                <span class="file-name" v-if="media.bannerDesktop.file">
                  {{ media.bannerDesktop.file.name }}
                </span>
              </b-field>
            </b-field>
            <b-field>
              <div class="image-preview">
                <div
                  class="image"
                  v-if="media.bannerDesktop.file || media.bannerDesktop.asset.url"
                >
                  <img
                    ref="bannerDesktop"
                    :src="media.bannerDesktop.file
                      ? getImageFromBlob(media.bannerDesktop.file, 'bannerDesktop')
                      : media.bannerDesktop.asset.url"
                    :style="media.bannerDesktop.recommendedSize | maxSize"
                  >
                </div>
              </div>
            </b-field>
            <b-field
              :label="
                `Banner Mobile (${recommendedSize(media.bannerMobile.recommendedSize)})`
                "
              >
              <b-field class="file">
                <b-upload v-model="media.bannerMobile.file" accept="image/*">
                  <a class="button is-primary">
                    <b-icon icon="upload"></b-icon>
                    <span>Click to upload</span>
                  </a>
                </b-upload>
                <span class="file-name" v-if="media.bannerMobile.file">
                  {{ media.bannerMobile.file.name }}
                </span>
              </b-field>
            </b-field>
            <b-field>
              <div class="image-preview">
                <div
                  class="image"
                  v-if="media.bannerMobile.file || media.bannerMobile.asset.url"
                >
                  <img
                    ref="bannerMobile"
                    :src="media.bannerMobile.file
                      ? getImageFromBlob(media.bannerMobile.file, 'bannerMobile')
                      : media.bannerMobile.asset.url"
                    :style="media.bannerMobile.recommendedSize | maxSize"
                  >
                </div>
              </div>
            </b-field>
          </b-tab-item>
          <b-tab-item label="SEO">
            <seo ref="seo" v-model="meta" :disabled="isDisabled" />
          </b-tab-item>
        </b-tabs>
      </section>
      <section class="section">
        <b-field>
          <button class="button is-success" :disabled="isDisabled">Save</button>
        </b-field>
      </section>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import VueCkeditor from 'vue-ckeditor5';
import uploadAdapter from '@/libs/uploadAdapter';
import Seo from '@/components/Seo.vue';

export default {
  name: 'my-article-info',
  components: {
    ckeditor: VueCkeditor.component,
    Seo,
  },
  filters: {
    maxSize(sizes) {
      return `max-width: ${sizes[0]}px; max-height: ${sizes[1]}px;`;
    },
  },
  data() {
    return {
      id: null,
      slug: null,
      title: null,
      description: null,
      detail: null,
      edtiorDetail: '',
      contentAt: null,
      activeAt: null,
      published: true,
      tags: [],
      meta: {
        title: null,
        description: null,
        ogTitle: null,
        ogDescription: null,
        ogMedia: 'image',
        ogMediaUrl: null,
        ogMediaType: null,
      },
      media: {
        thumbnail: {
          file: null,
          recommendedSize: [640, 480],
          asset: {
            type: 'thumbnail',
            url: null,
          },
        },
        bannerDesktop: {
          file: null,
          recommendedSize: [1600, 500],
          asset: {
            type: 'banner',
            channel: 'desktop',
            url: null,
          },
        },
        bannerMobile: {
          file: null,
          recommendedSize: [800, 1000],
          asset: {
            type: 'banner',
            channel: 'mobile',
            url: null,
          },
        },
      },
      editors: {
        classic: ClassicEditor,
      },
      filteredTags: [],
      fetching: false,
      saving: false,
      uploadAdapter,
    };
  },
  computed: {
    ...mapGetters('article', ['article']),
    ...mapGetters('tag', ['allTags']),
    breadcrumb() {
      const breadcrumb = [];
      breadcrumb.push({
        title: 'Article',
      });

      if (this.$route.params.id) {
        breadcrumb.push({
          title: this.title || this.id,
          to: {
            name: 'article',
          },
        }, {
          title: 'Edit',
        });
      } else {
        breadcrumb.push({
          title: 'Create',
        });
      }

      return breadcrumb;
    },
    isDisabled() {
      return this.fetching || this.saving;
    },
    hasUploadImage() {
      return this.media.thumbnail.file
        || this.media.bannerDesktop.file
        || this.media.bannerMobile.file;
    },
  },
  methods: {
    ...mapActions('article', [
      'getArticle',
      'saveArticle',
      'saveArticleMeta',
      'saveArticleAssets',
    ]),
    ...mapActions('upload', {
      upload: 'image',
    }),
    ...mapActions('tag', ['getAllTags']),
    getFilteredTags(text) {
      this.filteredTags = this.allTags.filter(tag => (
        tag.name
          .toString()
          .toLowerCase()
          .indexOf(text.toLowerCase()) >= 0
      ));
    },
    recommendedSize(size) {
      return `recommended size: ${size.join(' x ')}`;
    },
    onEditorInput(text) {
      this.detail = text;
    },
    parseData() {
      this.id = this.article.id;
      this.slug = this.article.slug;
      this.title = this.article.title;
      this.description = this.article.description;
      this.detail = this.article.detail;
      this.edtiorDetail = this.article.detail;
      this.contentAt = this.$moment(this.article.contentAt).toDate();
      this.activeAt = this.$moment(this.article.activeAt).toDate();
      this.published = this.article.published;
      this.tags = this.article.Tags.map(i => i.name);

      Object.assign(this.meta, this.article.Meta);

      Object.assign(
        this.media.thumbnail.asset,
        this.article.ArticleAssets.find(i => i.type === 'thumbnail') || {},
      );

      Object.assign(
        this.media.bannerDesktop.asset,
        this.article
          .ArticleAssets
          .find(i => i.type === 'banner' && i.channel === 'desktop') || {},
      );

      Object.assign(
        this.media.bannerMobile.asset,
        this.article
          .ArticleAssets
          .find(i => i.type === 'banner' && i.channel === 'mobile') || {},
      );
    },
    getImageFromBlob(file, ref) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.$refs[ref].src = e.target.result;
      };

      reader.readAsDataURL(file);
    },
    uploadMedia() {
      return Promise.all(Object.keys(this.media)
        .map((key) => {
          const { file, recommendedSize } = this.media[key];
          const options = {
            resize: {
              width: recommendedSize[0],
              height: recommendedSize[1],
            },
          };

          return file ? this.upload({ file, options }) : Promise.resolve(false);
        }));
    },
    saveAssets(assets, { id }) {
      return Promise.all(assets.map((item, key) => {
        if (!item) {
          return Promise.resolve();
        }

        const { asset } = this.media[Object.keys(this.media)[key]];
        const params = Object.assign({}, asset, {
          url: item.data.url,
        });

        return this.saveArticleAssets({ id, params });
      }));
    },
    async onSubmit() {
      const data = {
        id: this.id,
        slug: this.slug,
        title: this.title,
        description: this.description,
        detail: this.detail,
        contentAt: this.contentAt,
        activeAt: this.activeAt,
        published: this.published,
        tags: this.tags,
      };

      this.saving = true;

      const article = await this.saveArticle(data);
      const assets = await this.uploadMedia();

      await this.saveAssets(assets, article);

      if (this.meta.ogImage) {
        const response = await this.$refs.seo.uploadOgImage();
        this.meta.ogMediaUrl = response.data.url;
      }

      await this.saveArticleMeta({
        id: article.id,
        params: this.meta,
      });

      try {
        this.$toast.open({
          message: 'Article saved',
          type: 'is-success',
          duration: 5000,
        });

        if (!this.$route.params.id) {
          this.$router.push({ name: 'article', params: { id: article.id } });
        }

        this.saving = false;
      } catch (e) {
        this.$toast.open({
          message: 'Something wrong',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },
  },
  mounted() {
    this.getAllTags();

    if (this.$route.params.id) {
      this.fetching = true;
      this.getArticle(this.$route.params.id).then(() => {
        this.parseData();
      }).finally(() => {
        this.fetching = false;
      });
    } else {
      this.contentAt = new Date();
    }
  },
};
</script>

<style lang="sass" scoped>
.image-preview
  img
    object-fit: contain
    object-position: left
</style>
